import { useSyncExternalStore } from "react";

/**
 * A tiny per-message store for "your attachment didn't fit in the vault basket"
 * notices. The chat send path records how many attachments it had to drop for a
 * given message id (the vault storage limit was hit); the message row reads its
 * own count and renders an inline notice. Kept as an external store so the deeply
 * nested, virtualized message row can subscribe by id without prop-threading
 * through the whole timeline.
 */
const counts = new Map<string, number>();
const listeners = new Set<() => void>();

function emit(): void {
  for (const listener of listeners) listener();
}

/** Record that `count` attachments were dropped from the message with this id. */
export function markAttachmentDropped(messageId: string, count: number): void {
  if (!messageId || count <= 0) return;
  counts.set(messageId, count);
  emit();
}

/** Forget a message's dropped-attachment notice (e.g. on thread reset). */
export function clearAttachmentDropped(messageId: string): void {
  if (counts.delete(messageId)) emit();
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** How many attachments were dropped for this message (0 if none). */
export function useDroppedAttachmentCount(messageId: string): number {
  return useSyncExternalStore(
    subscribe,
    () => counts.get(messageId) ?? 0,
    () => 0,
  );
}
